/**
 * @fileoverview Rename dialog for Library files and folders.
 *
 * Pre-filled with the current name; for files the extension stays visible
 * and editable (the server strips path separators, and renameDocument
 * no-ops on an unchanged name).
 */

import { useEffect, useState } from "react";
import { useConvex } from "convex/react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { ConvexError } from "convex/values";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/ui/primitives/button";
import { Input } from "@/ui/primitives/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import type { LibraryMenuItem } from "./itemMenus";

interface RenameDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: LibraryMenuItem | null;
}

/** Small input dialog shared by file and folder renames. */
export function RenameDialog({ open, onOpenChange, item }: RenameDialogProps) {
  const convex = useConvex();
  const [name, setName] = useState("");
  const [isRenaming, setIsRenaming] = useState(false);

  // Re-seed the input whenever the dialog opens for a (new) item.
  useEffect(() => {
    if (open && item) setName(item.name);
  }, [open, item]);

  const handleRename = async () => {
    const trimmed = name.trim();
    if (!item || !trimmed || isRenaming) return;
    setIsRenaming(true);
    try {
      if (item.kind === "file" && item.documentId) {
        await convex.mutation(
          api.jobs.gatherInformation.documents.mutations.renameDocument,
          {
            documentId: item.documentId as Id<"documents">,
            fileName: trimmed,
          },
        );
      } else if (item.kind === "folder" && item.folderId) {
        await convex.mutation(
          api.jobs.gatherInformation.folders.mutations.renameFolder,
          { folderId: item.folderId as Id<"folders">, name: trimmed },
        );
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(
        err instanceof ConvexError && typeof err.data === "string"
          ? err.data
          : "Failed to rename",
      );
    } finally {
      setIsRenaming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>
            Rename {item?.kind === "folder" ? "folder" : "file"}
          </DialogTitle>
          <DialogDescription>
            Enter a new name for “{item?.name}”.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            void handleRename();
          }}
        >
          <Input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            onFocus={(e) => e.target.select()}
          />
          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || isRenaming}>
              {isRenaming && (
                <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
              )}
              Rename
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
